import {
  AssignGroup,
  AvatarImage,
  Bottom,
  Bottomright,
  Card,
  Check,
  Checkbox,
  ColorIcon,
  FirstCards,
  Group,
  IconDueDate,
  IconDiv,
  Label,
  LabelIcon,
  Milestone,
  OverLayDiv,
  Righttext,
  TextDate,
  TooltipDiv,
  Top,
  Topline
} from './styles';
import Avatar from '../avatar';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { StatusListInterface } from '../../interfaces/ProjectInterface';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AssignBoxV2 from '../dropdowns/assignPopup';
import { useTaskAssignActions } from '../dropdowns/assignPopup/assignActions';
import { calculateDueTime, isEmpty } from '../../helpers/common';
import { trackAnalyticActivity } from '../../services/analyticsService';
import { useHistory } from 'react-router-dom';
import {
  AVATAR_BORDER,
  COMMENT_TYPE,
  ICON_SIZE,
  LABEL_COLOUR_HASH,
  TASK_FILTER_PROPERTY_TYPE
} from '../../global/constants';
import Linkify from 'react-linkify';
import { createNewComment, updateTaskDetails } from '../../services/taskServices';
import dayjs from 'dayjs';
import { TASK_ANALYTICS } from '../../global/analyticsConstants';
import Tooltip from '../tooltip';
import { Dropdown, DropdownItem } from '../dropdown';
import { TaskModel } from '../../mobx/models/task';
import { rootStore } from '../../mobx/rootStore';
import TaskStatusDropdownV2 from '../dropdowns/taskStatusDropdown';
import { Milestone as MilestoneInterface, RawTaskGroup } from '../../mobx/interfaces/project';
import PriorityDropdownV2 from '../dropdowns/priorityDropdown';
import { observer } from 'mobx-react-lite';
import { TaskCount } from '../../pages/project/tasks/styles';
import { ProjectModel } from '../../mobx/models/project';
import CheckboxInput from '../input/checkboxInput';
import { ROUTES } from '../../routes/urls';

interface Props {
  taskDetails: TaskModel;
  loadData?: () => void;
  isTaskGrid?: boolean;
  currentProject?: ProjectModel;
  onClickCurrentTaskCheckbox?: (task: TaskModel, status: boolean) => void;
  onUpdateMilestone?: (task: TaskModel, milestone: MilestoneInterface) => void;
  onRemoveMilestone?: (task: TaskModel) => void;
  onUpdateStatus?: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  isView?: boolean;
}

const CalendarViewCard: React.FC<Props> = (Props) => {
  const {
    taskDetails,
    loadData,
    isTaskGrid,
    onClickCurrentTaskCheckbox,
    onUpdateMilestone,
    onRemoveMilestone,
    onUpdateStatus,
    currentProject,
    isView
  } = Props || {};

  const handleCheckboxClick = useCallback(() => {
    onClickCurrentTaskCheckbox?.(taskDetails, !taskDetails?.status);
  }, [onClickCurrentTaskCheckbox, taskDetails]);

  const handleUpdateStatus = useCallback(
    (status: StatusListInterface, previousStatus: StatusListInterface) => {
      onUpdateStatus?.(taskDetails, status, previousStatus);
    },
    [onUpdateStatus, taskDetails]
  );

  const handleRemoveMilestone = useCallback(() => {
    onRemoveMilestone?.(taskDetails);
  }, [onRemoveMilestone, taskDetails]);

  const handleUpdateMilestone = useCallback(
    (milestone: MilestoneInterface) => {
      onUpdateMilestone?.(taskDetails, milestone);
    },
    [onUpdateMilestone, taskDetails]
  );
  //States
  const [groupName, setGroupName] = useState<string>('');
  const [isAssignDropdownOpen, setIsAssignDropdownOpen] = useState(false);
  const assignActions = useTaskAssignActions(taskDetails);
  //Mobx variables
  const { workspaceStore, currentUser } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { taskGroups, milestones: milestoneList } = currentProject || {};

  //Other variables
  const history = useHistory();
  let statusDetails: StatusListInterface | undefined;

  if (currentProject?.statusEnable && currentProject?.statusData?.length > 0) {
    if (!taskDetails?.statusId) statusDetails = currentProject?.defaultStatus;
    else statusDetails = currentProject?.statusData?.find((x) => x['_id'] === taskDetails?.statusId);
  }

  const loadGroupName = useCallback(async () => {
    if (!isEmpty(taskDetails?.groupId) && !isEmpty(workspace?.id)) {
      const group = taskGroups?.find((x) => x?.id === taskDetails?.groupId);
      if (group) setGroupName(group?.name);
      else setGroupName('');
    }
  }, [taskDetails?.groupId, taskGroups, workspace?.id]);

  useEffect(() => {
    loadGroupName();
  }, [loadGroupName]);

  useEffect(() => {
    return () => {
      rootStore.taskStore.clearCurrentAssignBoxTaskId();
    };
  }, []);

  const onClickTaskDetails = useCallback(() => {
    const eventProperties = {
      source: 'task list',
      title: taskDetails?.name || ''
    };
    trackAnalyticActivity(TASK_ANALYTICS.VIEW, eventProperties);
    rootStore.taskStore.setCurrentTask(taskDetails?.id);
    history.push(ROUTES.TASK_DETAILS.replace(':id', taskDetails?.projectId).replace(':taskId', taskDetails?.id));
  }, [history, taskDetails]);

  const updateTaskGroup = useCallback(
    async (item: RawTaskGroup) => {
      if (item?.id !== taskDetails?.groupId) {
        const previousGroup = taskGroups?.find((x) => x?.id === taskDetails?.groupId);
        setGroupName(item?.name);
        taskDetails?.setGroup(item?.id);
        const payloadTask = { groupId: item?.id, projectId: taskDetails?.projectId };
        const result = await updateTaskDetails(taskDetails?.id, payloadTask);
        const commentResult = await createNewComment(taskDetails?.id, {
          Type: COMMENT_TYPE.CHANGE_GROUP,
          PreviousGroup: previousGroup?.name,
          Group: item?.name
        });

        if (result && commentResult) {
          if (loadData) loadData();
          trackAnalyticActivity(TASK_ANALYTICS.GROUP_CHANGED);
        }
      }
    },
    [loadData, taskDetails, taskGroups]
  );

  const getMilestoneName = useMemo(() => {
    if (currentProject?.isMilestone && !isEmpty(taskDetails?.milestoneId)) {
      const milestoneName = milestoneList?.find((item) => item?.id === taskDetails?.milestoneId);
      return milestoneName?.milestoneName;
    }
  }, [currentProject?.isMilestone, milestoneList, taskDetails?.milestoneId]);

  const dueResult = useMemo(() => {
    if (taskDetails?.dueDate) {
      return calculateDueTime(taskDetails.dueDate);
    }
  }, [taskDetails?.dueDate]);

  const renderTaskTitle = useCallback(
    (taskDetails: TaskModel) => {
      return (
        <abbr title={taskDetails?.name}>
          <Label className={isTaskGrid ? 'task-title' : ''}>
            {/* Add condition to check if ID display is enabled */}
            {!isEmpty(taskDetails.project?.identifier) &&
              !isView &&
              rootStore.projectStore.currentProject?.displayPropertiesFilterWithFirebase?.[
                TASK_FILTER_PROPERTY_TYPE.ID
              ] !== false && (
                <span>
                  {taskDetails.project?.identifier}-{taskDetails.taskNumber}
                </span>
              )}
            {taskDetails?.name || ''}
          </Label>
        </abbr>
      );
    },
    [isTaskGrid, isView]
  );

  return (
    <>
      <Card role='button' isTaskGrid={isTaskGrid} onClick={onClickTaskDetails}>
        <FirstCards isTaskGrid={isTaskGrid}>
          <Topline>
            <Top isTaskGrid={isTaskGrid}>
              {!isView &&
                isTaskGrid &&
                currentProject?.isMilestone &&
                taskDetails?.milestoneId &&
                rootStore.projectStore.currentProject?.displayPropertiesFilterWithFirebase?.[
                  TASK_FILTER_PROPERTY_TYPE.MILESTONE
                ] !== false && (
                  <Dropdown content={<Milestone>{getMilestoneName || 'No Milestone'}</Milestone>} trigger='click'>
                    <DropdownItem key={0} label='No Milestone' onClick={handleRemoveMilestone} />
                    {milestoneList?.map((item) => {
                      return (
                        <DropdownItem key={0} label={item?.milestoneName} onClick={() => handleUpdateMilestone(item)} />
                      );
                    })}
                  </Dropdown>
                )}
              {!isTaskGrid && !isView && taskDetails?.project?.isGroupEnabled && taskDetails?.groupId && (
                <Dropdown content={<Group>{groupName || 'Select Group'}</Group>} trigger='click'>
                  {taskGroups?.map((item) => {
                    return <DropdownItem label={item?.name} key={item?.id} onClick={() => updateTaskGroup(item)} />;
                  })}
                </Dropdown>
              )}

              {isTaskGrid ? (
                <Check>
                  {rootStore.projectStore.currentProject?.displayPropertiesFilterWithFirebase?.[
                    TASK_FILTER_PROPERTY_TYPE.STATUS
                  ] !== false && (
                    <>
                      {statusDetails && onUpdateStatus ? (
                        <TaskStatusDropdownV2
                          defaultStatus={currentProject?.defaultStatus}
                          statusList={currentProject?.statusData || []}
                          currentStatusId={taskDetails?.statusId}
                          isTaskCheckBox={true}
                          onUpdateStatus={handleUpdateStatus}
                          isIconButton={true}
                        />
                      ) : (
                        <Checkbox role='button' tabIndex={0} onClick={(e) => e.stopPropagation()}>
                          <CheckboxInput
                            checked={taskDetails?.status}
                            onChange={handleCheckboxClick}
                            checkboxType='task-checkbox'
                          />
                        </Checkbox>
                      )}
                    </>
                  )}

                  <Linkify
                    componentDecorator={(decoratedHref: string, decoratedText: string, key: number) => (
                      <a target='blank' href={decoratedHref} key={key}>
                        {decoratedText}
                      </a>
                    )}>
                    {renderTaskTitle(taskDetails)}
                  </Linkify>
                </Check>
              ) : (
                renderTaskTitle(taskDetails)
              )}
              {taskDetails?.labels?.length > 0 &&
                currentProject?.labelsEnabled &&
                rootStore.projectStore.currentProject?.displayPropertiesFilterWithFirebase?.[
                  TASK_FILTER_PROPERTY_TYPE.LABELS
                ] !== false && (
                  <IconDiv role='button' tabIndex={0} onClick={(e) => e.stopPropagation()}>
                    {taskDetails?.labels?.map((item, index: number) => {
                      return (
                        <ColorIcon key={item?.id} className={index !== 0 ? 'label-with-border' : ''}>
                          <LabelIcon color={`${LABEL_COLOUR_HASH[item?.color]}`} />
                          <Righttext>{item?.name}</Righttext>
                        </ColorIcon>
                      );
                    })}
                  </IconDiv>
                )}
            </Top>
          </Topline>

          <Bottom>
            <Bottomright>
              {taskDetails?.subTasks && taskDetails?.subTasks?.length > 0 && (
                <TaskCount>
                  {taskDetails?.subTasks?.filter((x) => x?.status)?.length}/{taskDetails?.subTasks?.length}
                </TaskCount>
              )}

              {/* priority dropdown for task grid */}
              {currentProject?.priority?.enabled &&
                isTaskGrid &&
                rootStore.projectStore.currentProject?.displayPropertiesFilterWithFirebase?.[
                  TASK_FILTER_PROPERTY_TYPE.PRIORITY
                ] !== false && (
                  <PriorityDropdownV2
                    taskData={taskDetails}
                    currentProject={currentProject}
                    isTaskCheckBox={true}
                    isIconButton={true}
                  />
                )}
              {rootStore.projectStore.currentProject?.displayPropertiesFilterWithFirebase?.[
                TASK_FILTER_PROPERTY_TYPE.ASSIGNEE
              ] !== false && (
                <AssignGroup className='assign' role='button' tabIndex={0} onClick={(e) => e.stopPropagation()}>
                  <ul>
                    {taskDetails?.users
                      ?.filter((item) => !isEmpty(item?.id))
                      ?.map((user) => {
                        return (
                          <li key={user?.id}>
                            <AvatarImage>
                              <Avatar
                                imgSrc={user?.profile_photo || ''}
                                name={user?.name ? user.name : 'U N'}
                                size={20}
                                borderVariant={AVATAR_BORDER.THIN}
                              />
                            </AvatarImage>
                          </li>
                        );
                      })}
                    <AssignBoxV2
                      setIsDropdownOpen={setIsAssignDropdownOpen}
                      projectUsers={currentProject?.users || []}
                      context={{ mode: 'task', currentTask: taskDetails }}
                      onAssign={assignActions.onAssign}
                      onUnassign={assignActions.onUnassign}
                      isMultiAssignee={currentProject?.multiAssignee}
                    />
                  </ul>
                </AssignGroup>
              )}
            </Bottomright>
            <Bottomright>
              {isTaskGrid &&
                taskDetails?.dueDate &&
                rootStore.projectStore.currentProject?.displayPropertiesFilterWithFirebase?.[
                  TASK_FILTER_PROPERTY_TYPE.DUE_DATE
                ] !== false && (
                  <Tooltip
                    title={
                      <>
                        <div>Due {dueResult?.dueParam}</div>
                        <div> Due on {dayjs(taskDetails?.dueDate).format(currentUser?.dateFormat)}</div>
                      </>
                    }
                    placement='bottom'
                    maxWidth={'max-content'}>
                    <TooltipDiv>
                      <TextDate isOverdue={dueResult?.overDue} className={taskDetails?.status ? 'task-completed' : ''}>
                        {dueResult?.dueTime}
                      </TextDate>

                      <IconDueDate>
                        {dueResult?.overDue && !taskDetails?.status ? (
                          <SVGIcon
                            name='tasklist-calendar-duedate-icon'
                            width={ICON_SIZE.XXL}
                            height={ICON_SIZE.XXL}
                            viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                            className='calendar-due-fill-color'
                          />
                        ) : (
                          <SVGIcon
                            name='tasklist-calendar-icon'
                            width={ICON_SIZE.XXL}
                            height={ICON_SIZE.XXL}
                            viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                            className='calendar-fill-color'
                          />
                        )}
                      </IconDueDate>
                    </TooltipDiv>
                  </Tooltip>
                )}
              {/* priority dropdown for calendar view */}
              {currentProject?.priority?.enabled &&
                !isTaskGrid &&
                rootStore.projectStore.currentProject?.displayPropertiesFilterWithFirebase?.[
                  TASK_FILTER_PROPERTY_TYPE.PRIORITY
                ] !== false && (
                  <PriorityDropdownV2
                    taskData={taskDetails}
                    currentProject={currentProject}
                    isTaskCheckBox={true}
                    isIconButton={true}
                  />
                )}
            </Bottomright>
          </Bottom>
        </FirstCards>
      </Card>
      {rootStore.taskStore.currentAssignBoxTaskId === taskDetails?.id && isAssignDropdownOpen && (
        <OverLayDiv
          className='dropdown-overlay'
          role='button'
          tabIndex={0}
          onClick={() => {
            setIsAssignDropdownOpen(false);
            rootStore.taskStore.clearCurrentAssignBoxTaskId();
          }}
        />
      )}
    </>
  );
};

export default observer(CalendarViewCard);
